import React, { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import useUser from '@hooks/user/use-user';
import useUpdateEmailPreferences from '@hooks/user/use-update-email-preferences';
import Switch from '@components/catalog/switch/switch';
import Button from '@components/atoms/button/button';
import Icon from '@components/atoms/icon/icon';
import { useAnalytics } from '@context/analytics/analytics.context';
import {
  accountSettingsSaveNotificationsEventPayload,
  accountSettingsToggleNotificationEventPayload,
} from '@tracking/account-settings/account-settings-tracking';
import { AccountSettingsToggleNotificationEvent } from '@interfaces/models/internal/analytics-event/account-settings-event';
import styles from './account-notifications.module.scss';

type NotificationPreferences = {
  newsSales: boolean;
  sellingAdvice: boolean;
  updatesOnItems: boolean;
};

const DEFAULT_PREFERENCES: NotificationPreferences = {
  newsSales: false,
  sellingAdvice: false,
  updatesOnItems: false,
};

const NOTIFICATION_ITEMS: {
  key: keyof NotificationPreferences;
  labelKey: string;
  dataCy: string;
  trackingProperty: AccountSettingsToggleNotificationEvent['property'];
}[] = [
  {
    key: 'updatesOnItems',
    labelKey: 'ACCOUNT_SETTINGS.EMAIL_PREF.UPDATES_ON_ITEMS',
    dataCy: 'account-notifications-updates-on-items-switch',
    trackingProperty: 'Items I like',
  },
  {
    key: 'sellingAdvice',
    labelKey: 'ACCOUNT_SETTINGS.EMAIL_PREF.SELLING_ADVICE',
    dataCy: 'account-notifications-selling-advice-switch',
    trackingProperty: 'My listings',
  },
  {
    key: 'newsSales',
    labelKey: 'ACCOUNT_SETTINGS.EMAIL_PREF.NEWS_SALES',
    dataCy: 'account-notifications-news-sales-switch',
    trackingProperty: 'Sales and News',
  },
];

const AccountNotifications: React.FC = () => {
  const { t } = useTranslation('account-settings');
  const { t: tCommon } = useTranslation('common');
  const { user } = useUser();
  const { sendEvent } = useAnalytics();
  const { mutateAsync: updateEmailPreferences, isLoading: isSaving } = useUpdateEmailPreferences(user?.id ?? '');
  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULT_PREFERENCES);
  const [savedPreferences, setSavedPreferences] = useState<NotificationPreferences>(DEFAULT_PREFERENCES);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    if (!user) {
      return;
    }
    const prefs: NotificationPreferences = {
      newsSales: user.isSubscribedToNewsletter ?? false,
      sellingAdvice: user.hasLegitInterestSeller ?? false,
      updatesOnItems: user.hasLegitInterestBuyer ?? false,
    };
    setPreferences(prefs);
    setSavedPreferences(prefs);
  }, [user?.id, user?.isSubscribedToNewsletter, user?.hasLegitInterestSeller, user?.hasLegitInterestBuyer]);

  if (!user) {
    return null;
  }

  const hasChanges =
    preferences.newsSales !== savedPreferences.newsSales ||
    preferences.sellingAdvice !== savedPreferences.sellingAdvice ||
    preferences.updatesOnItems !== savedPreferences.updatesOnItems;

  const handleChange =
    (key: keyof NotificationPreferences, trackingProperty: AccountSettingsToggleNotificationEvent['property']) =>
    (checked: boolean) => {
      setApiError(null);
      setIsSaved(false);
      setPreferences((prev) => ({ ...prev, [key]: checked }));
      const action = checked ? 'on' : 'off';
      sendEvent({
        type: 'event',
        payload: {
          ...accountSettingsToggleNotificationEventPayload,
          event: `my_account_pref_${action}`,
          action,
          property: trackingProperty,
        },
      });
    };

  const handleSave = async () => {
    setApiError(null);
    try {
      await updateEmailPreferences({
        isSubscribedToNewsletter: preferences.newsSales,
        hasLegitInterestSeller: preferences.sellingAdvice,
        hasLegitInterestBuyer: preferences.updatesOnItems,
      });
      setSavedPreferences(preferences);
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2000);
      sendEvent({ type: 'event', payload: accountSettingsSaveNotificationsEventPayload });
    } catch (error) {
      setApiError((error?.response?.data?.errors?.detail as string) ?? tCommon('ALERT.GENERIC_ERROR'));
    }
  };

  return (
    <div
      className={styles.accountNotifications}
      data-cy="account-notifications-section"
    >
      <h2 className={styles.accountNotifications__title}>{t('ACCOUNT_SETTINGS.MENU.EMAIL')}</h2>
      <ul className={styles.accountNotifications__list}>
        {NOTIFICATION_ITEMS.map(({ key, labelKey, dataCy, trackingProperty }) => (
          <li
            key={key}
            className={styles.accountNotifications__item}
          >
            <span
              id={`notif-${key}`}
              className={styles.accountNotifications__label}
            >
              {t(labelKey)}
            </span>
            <Switch
              checked={preferences[key]}
              onChange={handleChange(key, trackingProperty)}
              ariaLabelledby={`notif-${key}`}
              dataCyElementLocator={dataCy}
            />
          </li>
        ))}
      </ul>
      {apiError && (
        <p
          role="alert"
          className={styles.accountNotifications__error}
        >
          <Icon
            name="warning-default"
            ariaHidden
          />
          {apiError}
        </p>
      )}
      <div className={styles.accountNotifications__actions}>
        <Button
          variant="primary"
          type="button"
          size="large"
          disabled={isSaving || !hasChanges}
          onClick={handleSave}
          data-cy="account-notifications-save-button"
        >
          {t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.SAVE')}
        </Button>
        {isSaved && (
          <span
            className={styles.accountNotifications__saved}
            role="status"
            aria-live="polite"
          >
            {t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.SAVE_CONFIRMATION')}
          </span>
        )}
      </div>
    </div>
  );
};

export default AccountNotifications;
